// ===========================================
// Enrollment by Type Chart Component
// ===========================================
// Pie chart showing enrollment distribution by lesson type
// Highlights HYBRID as core differentiator
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import { Paper, Typography, Box, Skeleton, Alert } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { EnrollmentByTypeRecord } from '../../api/reports.api';

interface EnrollmentByTypeChartProps {
  data?: EnrollmentByTypeRecord[];
  isLoading: boolean;
}

// Brand colors for each lesson type
const LESSON_TYPE_COLORS: { [key: string]: string } = {
  HYBRID: '#4580E4', // Primary Blue - HIGHLIGHTED
  INDIVIDUAL: '#96DAC9', // Mint
  GROUP: '#FFCE00', // Yellow
  BAND: '#FFAE9E', // Coral
};

// Fallback color array for dynamic lesson types
const DEFAULT_COLORS = ['#4580E4', '#FFCE00', '#96DAC9', '#FFAE9E', '#9B59B6'];

const EnrollmentByTypeChart: React.FC<EnrollmentByTypeChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Enrollment Distribution by Lesson Type
        </Typography>
        <Skeleton variant="rectangular" height={400} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Enrollment Distribution by Lesson Type
        </Typography>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">
            No enrollment by type data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  const chartData = data.map((type) => ({
    name: type.lessonTypeName,
    value: type.activeCount,
    percentage: type.percentage,
  }));

  const totalEnrollments = data.reduce((sum, type) => sum + type.activeCount, 0);
  const hybridData = data.find((type) => type.lessonTypeName === 'HYBRID');

  // Custom label to emphasize HYBRID
  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
    percentage,
  }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={name === 'HYBRID' ? 'white' : 'black'}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontWeight={name === 'HYBRID' ? 'bold' : 'normal'}
        fontSize={name === 'HYBRID' ? 14 : 12}
      >
        {`${name} (${percentage.toFixed(1)}%)`}
      </text>
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Enrollment Distribution by Lesson Type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Total Active Enrollments: {totalEnrollments}
      </Typography>

      {/* Highlight HYBRID as core differentiator */}
      {hybridData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Music 'n Me's Core Differentiator:</strong> {hybridData.activeCount} students (
            {hybridData.percentage.toFixed(1)}%) are enrolled in HYBRID lessons - our unique model
            combining group and individual instruction!
          </Typography>
        </Alert>
      )}

      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={LESSON_TYPE_COLORS[entry.name] || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                strokeWidth={entry.name === 'HYBRID' ? 3 : 1}
                stroke={entry.name === 'HYBRID' ? '#3899ec' : '#fff'}
              />
            ))}
          </Pie>
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length > 0) {
                const data = payload[0].payload;
                return (
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2">{data.name}</Typography>
                    <Typography variant="body2">
                      Active Enrollments: {data.value}
                    </Typography>
                    <Typography variant="body2">
                      Percentage: {data.percentage.toFixed(1)}%
                    </Typography>
                    {data.name === 'HYBRID' && (
                      <Typography variant="caption" display="block" color="primary" fontWeight="bold">
                        Core Differentiator
                      </Typography>
                    )}
                  </Box>
                );
              }
              return null;
            }}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default EnrollmentByTypeChart;
